/** What a strategy's capital has actually done.
 *
 * Replaces a card that read `Rs 2,00,000 + this month's P&L`: a constant plus
 * a number that reset every month, which cannot tell a balance that GREW from
 * one that was FUNDED. Operator, 2026-09-23: *"As I already told include from
 * monday deposite and withdraw money"*, *"Per strategy for POI and EMI"*.
 *
 * The figures come from `/api/capital`, which folds every closed trade through
 * his own funding rules (`nss-research/docs/capital.md`). The base grows 1.5x
 * at each doubling and never falls back, so it is shown beside what the
 * account started at.
 *
 * ⚠️ DUE is what the rules call for and the ledger has not recorded. It is a
 * prompt, never a claim that money moved — recording is one deliberate click,
 * because the app must not say a transfer happened when it did not.
 */

import { useState, type ReactElement } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { capitalApi, type CashDue, type CapitalAccount } from "@/lib/capitalApi";
import { describeError } from "@/lib/http";

function rupees(n: number): string {
  return `Rs ${Math.round(n).toLocaleString("en-IN")}`;
}

function signed(n: number): string {
  return `${n >= 0 ? "+" : "-"}${rupees(Math.abs(n))}`;
}

function tone(n: number): string {
  if (n > 0) return "text-success";
  return n < 0 ? "text-danger" : "text-ink";
}

function Row({
  label,
  value,
  sub,
  valueTone,
}: Readonly<{ label: string; value: string; sub?: string; valueTone?: string }>): ReactElement {
  return (
    <div className="flex items-baseline justify-between gap-2 py-0.5">
      <span className="text-xs text-ink-secondary">
        {label}
        {sub !== undefined && <span className="ml-1 text-[10px] text-ink-muted">{sub}</span>}
      </span>
      <span className={`font-mono text-sm tabular-nums ${valueTone ?? "text-ink"}`}>{value}</span>
    </div>
  );
}

function DueRow({
  due,
  onRecord,
  saving,
}: Readonly<{ due: CashDue; onRecord: (d: CashDue) => void; saving: boolean }>): ReactElement {
  const deposit = due.flow === "DEPOSIT";
  return (
    <div className="mt-1.5 flex items-center justify-between gap-2 rounded border border-warning-border bg-warning-bg px-2 py-1.5">
      <div className="min-w-0">
        <div className="text-xs font-medium text-warning">
          {deposit ? "Deposit" : "Withdraw"} {rupees(due.amount)}
        </div>
        <div className="truncate text-[10px] text-ink-muted">
          {due.on} · {due.why}
        </div>
      </div>
      <button
        type="button"
        className="btn btn-secondary shrink-0 text-[11px]"
        disabled={saving}
        onClick={() => {
          onRecord(due);
        }}
      >
        Record
      </button>
    </div>
  );
}

/** The hub shows one NET line; the detail page shows every dated movement.
 *
 * Operator, 2026-09-23: *"in main page why to show so many deposite or witddraw
 * show net there"*. Six prompts whose net effect was zero — every top-up in the
 * list had already been repaid two rows later — is noise on a page that exists
 * to be glanced at. The dated rows still matter when acting on them, so they
 * stay one click away, where the Record buttons are.
 */
function NetDue({ due }: Readonly<{ due: CashDue[] }>): ReactElement | null {
  if (due.length === 0) return null;
  const net = due.reduce((sum, d) => sum + (d.flow === "DEPOSIT" ? d.amount : -d.amount), 0);
  if (Math.round(net) === 0) {
    return (
      <div className="mt-1.5 text-[11px] text-ink-muted">
        {due.length} funding steps, netting to nothing — see detail
      </div>
    );
  }
  const deposit = net > 0;
  return (
    <div className="mt-1.5 rounded border border-warning-border bg-warning-bg px-2 py-1.5">
      <div className="text-xs font-medium text-warning">
        Net {deposit ? "deposit" : "withdrawal"} {rupees(Math.abs(net))}
      </div>
      <div className="text-[10px] text-ink-muted">
        across {due.length} step{due.length === 1 ? "" : "s"} — record them in detail
      </div>
    </div>
  );
}

function DueArea({
  due,
  compact,
  saving,
  onRecord,
}: Readonly<{
  due: CashDue[];
  compact: boolean;
  saving: boolean;
  onRecord: (d: CashDue) => void;
}>): ReactElement | null {
  if (compact) return <NetDue due={due} />;
  return (
    <>
      {due.map((d) => (
        <DueRow
          key={`${d.on}-${d.flow}-${String(d.amount)}`}
          due={d}
          saving={saving}
          onRecord={onRecord}
        />
      ))}
    </>
  );
}

function Figures({ account }: Readonly<{ account: CapitalAccount }>): ReactElement {
  const grown = account.base !== account.started;
  // ⚠️ THE ACCOUNT IGNORES THE MONTH FILTER, and must: the funding rules only
  // make sense run forward from the first trade. Starting them at a month
  // boundary demands deposits the strategy's own earlier profit already covers
  // (operator, 2026-09-23: *"Now POI net profit is 4000 ... so why to
  // deposite"*). It therefore disagrees with the Realised card beside it —
  // EMA's September tab shows +1,31,962 while the account shows +2,24,517,
  // because August made +92,555. Said out loud here rather than left to be
  // discovered: *"How profit of EMI is 224517 I already share image its
  // 137362"*.
  const since = account.first_session ?? "the first trade";
  return (
    <>
      <div className="mt-1 text-2xl font-medium">{rupees(account.balance)}</div>
      <div className="text-xs text-ink-muted">
        Balance · base {rupees(account.base)}
        {grown && <span className="text-success"> (from {rupees(account.started)})</span>}
      </div>
      <div className="mt-0.5 text-[10px] text-ink-muted">
        Whole account since {since} — not the selected month
      </div>
      <div className="mt-3 border-t border-line pt-2">
        <Row
          label="Profit"
          value={signed(account.profit)}
          sub={`net of charges, since ${since}`}
          valueTone={tone(account.profit)}
        />
        <Row label="Deposited" value={rupees(account.deposited)} />
        <Row label="Withdrawn" value={rupees(account.withdrawn)} />
        {account.outstanding > 0 && (
          <Row
            label="Top-up outstanding"
            value={rupees(account.outstanding)}
            sub="to repay"
            valueTone="text-warning"
          />
        )}
        <Row label="Sessions" value={String(account.sessions)} sub="traded" />
      </div>
    </>
  );
}

/** Records one due movement, then refetches the account. Split out only to
 * keep `CapitalCard` inside the line limit. */
function useRecordMovement(strategyId: string, onFailure: (message: string) => void) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (due: CashDue) =>
      capitalApi.record({
        strategy_id: strategyId,
        on: due.on,
        flow: due.flow,
        amount: due.amount,
        note: due.why,
      }),
    onSuccess: async () => {
      onFailure("");
      await client.invalidateQueries({ queryKey: ["capital", strategyId] });
    },
    onError: (err: unknown) => {
      onFailure(describeError(err));
    },
  });
}

export function CapitalCard({
  strategyId,
  compact = false,
}: Readonly<{ strategyId: string; compact?: boolean }>): ReactElement {
  const [failed, setFailed] = useState("");
  const account = useQuery({
    queryKey: ["capital", strategyId],
    queryFn: () => capitalApi.forStrategy(strategyId),
  });
  const record = useRecordMovement(strategyId, setFailed);

  return (
    <div className="stat-card">
      <div className="text-xs uppercase tracking-wider text-ink-secondary">Capital</div>
      {account.isPending && <p className="mt-2 text-sm text-ink-muted">Loading…</p>}
      {account.isError && (
        <p className="mt-2 text-sm text-danger">Failed to load: {describeError(account.error)}</p>
      )}
      {account.data && (
        <>
          <Figures account={account.data} />
          <DueArea
            due={account.data.due}
            compact={compact}
            saving={record.isPending}
            onRecord={(d) => {
              record.mutate(d);
            }}
          />
          {failed !== "" && <p className="mt-1 text-[11px] text-danger">{failed}</p>}
        </>
      )}
    </div>
  );
}
